import { noop } from "lodash";
import { forwardRef } from "react";
import { useFormContext } from "react-hook-form";

import { cn } from "@/lib/utils";

import type { Props as AccountDropdownProps } from "@/components/shared/select-inputs/accounts-dropdown";
import {
  AutoComplete,
  type AutoCompleteProps,
  type Option as AutocompleteOption,
} from "@/components/ui/autocomplete";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

export type Props = {
  name: string;
  label?: string;
  options: AutocompleteOption[];
  autoFocus?: boolean;
  description?: string;
  direction?: "horizontal" | "vertical";
  fullWidth?: AccountDropdownProps["fullWidth"];
  className?: AccountDropdownProps["className"];
  placeholder?: AccountDropdownProps["placeholder"];
  containerClassName?: React.HTMLAttributes<HTMLDivElement>["className"];
  sideOffset?: number;
  allowReset?: boolean;
  allowUnknownAccount?: boolean;
  readOnly?: boolean;
  container?: React.RefObject<HTMLElement>;
  modal?: boolean;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  filterBy?: AutoCompleteProps["filterBy"];
  EmptyListComponent?: ({
    inputValue,
  }: { inputValue: string }) => React.ReactElement;
  side?: "top" | "right" | "bottom" | "left";
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: (value: AutocompleteOption["value"]) => void;
  showEmptyLabel?: boolean;
  filterOnOpen?: boolean;
  nullable?: boolean;
};

export type Option = AutocompleteOption;

export const AutocompleteInput = forwardRef<HTMLInputElement, Props>(
  (
    {
      label,
      name,
      side,
      sideOffset,
      options,
      placeholder,
      description,
      containerClassName,
      direction = "vertical",
      className,
      filterBy,
      onKeyDown,
      EmptyListComponent,
      autoFocus,
      onFocus = noop,
      onBlur = noop,
      showEmptyLabel,
      filterOnOpen,
      nullable,
    },
    ref,
  ) => {
    const form = useFormContext();

    return (
      <FormField
        control={form.control}
        name={name}
        render={({ field }) => {
          return (
            <FormItem>
              <div
                className={cn(
                  direction === "horizontal" &&
                    "flex flex-row items-center space-x-4",
                  containerClassName,
                )}
              >
                {label && <FormLabel>{label}</FormLabel>}
                <FormControl>
                  <div className="w-full flex flex-row gap-1">
                    <AutoComplete
                      ref={ref}
                      options={options}
                      value={field.value}
                      className={className}
                      placeholder={placeholder}
                      onKeyDown={onKeyDown}
                      filterBy={filterBy}
                      onValueChange={(value) => {
                        field.onChange(value?.value ?? null);
                      }}
                      EmptyListComponent={EmptyListComponent}
                      onBlur={(event) => {
                        onBlur(event);
                        field.onBlur();
                      }}
                      onFocus={() => {
                        onFocus(field.value);
                      }}
                      side={side}
                      sideOffset={sideOffset}
                      autoFocus={autoFocus}
                      showEmptyLabel={showEmptyLabel}
                      filterOnOpen={filterOnOpen}
                      nullable={nullable}
                    />
                  </div>
                </FormControl>
                {description && (
                  <FormDescription>{description}</FormDescription>
                )}
                <FormMessage />
              </div>
            </FormItem>
          );
        }}
      />
    );
  },
);
